import React, { FC } from 'react';
import { View, TouchableOpacity } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';

import { scale } from '@components/frame';
import { isPhone, navigateTo } from '@helpers';
import { font } from '@styles/fonts';
import ColorWrapper from '@styles/colors';
import Svg from '@styles/icons/svg';
import Button from '@styles/buttons';

import MachinesHeader from '@v2/pages/machines/common/components/Header/MachinesHeaderV2/MachinesHeaderV2';
import MachineNameAndStatus from '@v2/pages/machines/common/components/MachineNameAndStatus/MachineNameAndStatus';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { Machine } from '@interfaces/machines';
import { EditingPartData } from '@interfaces/base';
import { setActionsModalOpen, setIsMachinesSelectModalOpen } from '@reducers/machinesV2';
import { setIsEditModeForPartSwitchOn } from '@reducers/details';
import { addProductionInformationV2 } from '@reducers/manufacture';
import {
  INFO_SCREEN_MARGIN_TOP,
  MACHINES_SCREENS_TOP_BLOCK_HEIGHT_NOT_PHONE,
  SCREEN_PADDING_HORIZONTAL,
  SCREEN_PADDING_VERTICAL_FOR_HEADER,
  TEST_IDS,
} from '../../../../../../common/constants/base.constants';

interface MachinesScreenHeaderProps {
  selectedItem: Machine;
  setIsMachinesSelectModalOpen: (state: boolean) => void;
  editingPartData: EditingPartData;
  setIsEditModeForPartSwitchOn: (state: boolean) => void;
  addProductionInformationV2: (params: Record<string, unknown>) => Promise<void>;
  isEditModeForPartSwitchOn: boolean;
}

const MachinesScreenHeaderComponent: FC<MachinesScreenHeaderProps> = ({
  selectedItem,
  setIsMachinesSelectModalOpen,
  C,
  setActionsModalOpen,
  featureFlags,
  panelViewMode,
  addProductionInformationV2,
  editingPartData,
  setIsEditModeForPartSwitchOn,
  isEditModeForPartSwitchOn,
}) => {
  const { isAllSelected } = useHandleParams();

  const shouldHideOpenOperatorPanelButton = isPhone || !featureFlags['machine-operator-panel'] || isAllSelected;

  const onOperatorPanelButtonPress = () => {
    navigateTo('OperatorPanel', { id: selectedItem.id || selectedItem.machineId, hideNcFilePopup: true });
  };

  const onDoneEditingButtonPress = async () => {
    setIsEditModeForPartSwitchOn(false);
    await addProductionInformationV2(editingPartData as Record<string, unknown>);
  };

  return (
    <View style={{ paddingBottom: scale(SCREEN_PADDING_VERTICAL_FOR_HEADER), paddingHorizontal: !isPhone && scale(SCREEN_PADDING_HORIZONTAL) }}>
      <View
        style={[
          {
            marginTop: scale(INFO_SCREEN_MARGIN_TOP),
            marginBottom: scale(18),
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          },
          !isPhone && { height: scale(MACHINES_SCREENS_TOP_BLOCK_HEIGHT_NOT_PHONE) },
        ]}
      >
        <MachineNameAndStatus {...{ selectedItem, setIsMachinesSelectModalOpen }} />
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(22) }}>
          {isEditModeForPartSwitchOn && (
            <Button {...{ variant: 'M', onPress: onDoneEditingButtonPress, borderRadius: scale(14.5), loading: false }}>{i18n.t('Done')}</Button>
          )}
          {!shouldHideOpenOperatorPanelButton && !isEditModeForPartSwitchOn && (
            <Button {...{ variant: 'M', type: 'Secondary', onPress: onOperatorPanelButtonPress, testID: TEST_IDS.operatorPanel.entry.openButton }}>
              {i18n.t('Open Operator Panel')}
            </Button>
          )}
          <TouchableOpacity onPress={() => setActionsModalOpen(true)}>
            <Svg icon="more" width={scale(24)} height={scale(24)} color={C('Button/Light/Secondary Text')} />
          </TouchableOpacity>
        </View>
      </View>
      <MachinesHeader {...{ panelViewMode: false, noMachine: false }} />
    </View>
  );
};

export const MachinesScreenHeader = connect(
  ({ machines: { selectedItem }, user: { featureFlags }, details: { editingPartData, isEditModeForPartSwitchOn } }) => ({
    selectedItem,
    featureFlags,
    editingPartData,
    isEditModeForPartSwitchOn,
  }),
  {
    setIsMachinesSelectModalOpen,
    setActionsModalOpen,
    setIsEditModeForPartSwitchOn,
    addProductionInformationV2,
  }
)(ColorWrapper(MachinesScreenHeaderComponent));
